import { compile } from 'path-to-regexp'

let defaults = {
  coursenum: window.location.pathname.split('/')[3],
}
let makeUrlThunk =
  ptn =>
  (params = {}) =>
    ptn({ ...defaults, ...params })

export let badges = {
  create: makeUrlThunk(compile('/instructor/badges.create/:coursenum/')),
  update: makeUrlThunk(compile('/instructor/badges.update/:coursenum/:id')),
  show: makeUrlThunk(compile('/instructor/badges.badge_info/:coursenum/:id')),
  assessments: makeUrlThunk(
    compile('/instructor/badges.get_assessments_json/:coursenum/')
  ),
  chapters: makeUrlThunk(
    compile('/instructor/badges.get_chapters_json/:coursenum/')
  ),
  videos: makeUrlThunk(
    compile('/instructor/badges.get_videos_json/:coursenum/')
  ),
}

export let certificates = {
  create: makeUrlThunk(compile('/instructor/certificates.create/:coursenum/')),
  update: makeUrlThunk(
    compile('/instructor/certificates.update/:coursenum/:id')
  ),
  show: makeUrlThunk(
    compile('/instructor/certificates.cert_info/:coursenum/:id')
  ),
  assessments: makeUrlThunk(
    compile('/instructor/certificates.get_assessments_json/:coursenum/')
  ),
  chapters: makeUrlThunk(
    compile('/instructor/certificates.get_chapters_json/:coursenum/')
  ),
}

export let customizeContent = {
  save: makeUrlThunk(compile('/instructor/customize_content/:coursenum')),
}

export let dashboard = {
  getOverallStatisticsURL: makeUrlThunk(
    compile('/instructor/dashboard.performance_statistics/:coursenum/')
  ),
  getEngagementStatisticsURL: makeUrlThunk(
    compile('/instructor/dashboard.engagement_statistics/:coursenum/')
  ),
  getAssessmentAnalyticsURL: makeUrlThunk(
    compile('/instructor/assessment.json_analytics/:coursenum/')
  ),
}

export let gradebook = {
  index: makeUrlThunk(
    compile('/instructor/gradebook.gradebook_api_data/:coursenum/')
  ),
  download: makeUrlThunk(
    compile('/instructor/gradebook.download_gradebook/:coursenum/')
  ),
  popover: makeUrlThunk(
    compile(
      '/instructor/gradebook.generate_popover/:coursenum/:activityID/:studentID'
    )
  ),
  sections: makeUrlThunk(
    compile('/instructor/gradebook.course_sections_api_data/:coursenum/')
  ),
}

export let lms = {
  show: makeUrlThunk(
    compile('/instructor/lms.get_course_settings/:coursenum/')
  ),
  save: makeUrlThunk(
    compile('/instructor/lms.update_course_settings/:coursenum/')
  ),
  courses: makeUrlThunk(compile('/instructor/lms.get_lms_courses/:coursenum/')),
  token: makeUrlThunk(compile('/instructor/lms.get_course_token/:coursenum/')),
  assessments: makeUrlThunk(
    compile('/instructor/lms.get_assessment_data/:coursenum/')
  ),
  syncAssessments: makeUrlThunk(
    compile('/instructor/lms.sync_assessment/:coursenum/')
  ),
  bsAKP: makeUrlThunk(compile('/instructor/lms.brightspace_akp/:coursenum/')),
  authorizeV3: makeUrlThunk(
    compile('/links/client_canvas.authorize_v3/:coursenum/')
  ),
  verifyCanvasToken: makeUrlThunk(
    compile('/links/client_canvas.test_access_token/:coursenum/')
  ),
  canvasAssignments: makeUrlThunk(
    compile('/links/client_canvas.get_canvas_assignments/:coursenum/')
  ),
  externalTools: makeUrlThunk(
    compile('/links/client_canvas.get_external_tools/:coursenum/')
  ),
  createCourseLink: makeUrlThunk(
    compile('/links/client_canvas.create_unified_course_link/:coursenum/')
  ),
  createCanvasAssignment: makeUrlThunk(
    compile('/links/client_canvas.create_canvas_assignment/:coursenum/:id/')
  ),
  deleteCanvasAssignment: makeUrlThunk(
    compile('/links/client_canvas.delete_canvas_assignment/:coursenum/:id/')
  ),
}

export let lti = {
  getAssessmentGroups: makeUrlThunk(
    compile('/links/lti.get_item_groups/:coursenum/no_chapters/')
  ),
  getItemGroups: makeUrlThunk(
    compile('/links/lti.get_item_groups/:coursenum/')
  ),
  postItemGroups: makeUrlThunk(
    compile('/links/lti.post_item_groups/:coursenum/')
  ),
}

export let ltiLogs = {
  getLtiLogs: makeUrlThunk(
    compile('/account/support_dashboard.get_log_data/:coursenum/')
  ),
}

export let notebook = {
  filterHighlights: makeUrlThunk(
    compile('/reader/notebook.filter_highlights/:coursenum/')
  ),
  editNote: makeUrlThunk(compile('/reader/notebook.edit_note/:coursenum/')),
  deleteHighlight: makeUrlThunk(
    compile('/reader/notebook.delete_highlight/:coursenum/')
  ),
}

export let analytics = {
  progress(args) {
    let { coursenum, chapterFilter, studentType } = { ...defaults, ...args }
    return `/instructor/progress.analytics/${coursenum}/${chapterFilter}/${studentType}/`
  },
}

export let customCss = {
  show: makeUrlThunk(compile('/account/admin_css.get_template/:id')),
  save(args) {
    let { id } = { ...defaults, ...args }
    return `/account/admin_css.save/${id}`
  },
}

export let errorReports = {
  save: makeUrlThunk(
    compile('/account/admin_error_reports.update_report/-/:reportId')
  ),
}

export let reader = {
  search: makeUrlThunk(compile('/reader/search/:coursenum/')),
  retrieveAnalytics(args) {
    let { coursenum, chapterID, pageID } = { ...defaults, ...args }
    return `/reader/web.get_navigation_toolbar_data/${coursenum}/${chapterID}/${pageID}/`
  },
  recordAnalytics(args) {
    let { coursenum, chapterID, pageID } = { ...defaults, ...args }
    return `/reader/web.record_navigation_toolbar_analytics/${coursenum}/${chapterID}/${pageID}`
  },
}

export let studentExceptions = {
  postAccessDate: makeUrlThunk(
    compile(
      '/instructor/student_exceptions.update_availability_rule/:coursenum/:activityId/:studentId/'
    )
  ),
  postDueDate: makeUrlThunk(
    compile(
      '/instructor/student_exceptions.update_due_date_rule/:coursenum/:activityId/:studentId/'
    )
  ),
  postTimeLimit: makeUrlThunk(
    compile(
      '/instructor/student_exceptions.update_time_limit_rule/:coursenum/:activityId/:studentId/'
    )
  ),
}

const _bulkUploadGradesPath = compile(
  '/links/client_canvas.upload_grades/:coursenum/'
)

export let people = {
  newSection: makeUrlThunk(
    compile('/instructor/people.new_section/:coursenum/')
  ),
  tempAccess: makeUrlThunk(
    compile('/instructor/people.temp_access/:coursenum/')
  ),
  addPeople: makeUrlThunk(compile('/instructor/people.add_people/:coursenum/')),
  editSectionName: makeUrlThunk(
    compile('/instructor/people.edit_section_name/:coursenum/')
  ),
  removeSection: makeUrlThunk(
    compile('/instructor/people.remove_section/:coursenum/')
  ),
  setExpirationDate: makeUrlThunk(
    compile('/instructor/people.set_section_expiration_date/:coursenum/')
  ),
  archiveMembers: makeUrlThunk(
    compile('/instructor/people.archive_course_members/:coursenum/')
  ),
  removeCourseMember: makeUrlThunk(
    compile('/instructor/people.remove_course_member/:coursenum/')
  ),
  fixLmsId: makeUrlThunk(compile('/instructor/people.fix_lms_id/:coursenum/')),
  uploadGradesToLms: makeUrlThunk(
    compile('/instructor/people.upload_lms_grades/:coursenum/')
  ),
  forceReadingAnalytics: makeUrlThunk(
    compile('/instructor/people.force_reading_analytics/:coursenum/')
  ),
  unarchiveCourseMember: makeUrlThunk(
    compile('/instructor/people.unarchive_course_member/:coursenum/')
  ),
  forceVideoAnalytics: makeUrlThunk(
    compile('/instructor/people.force_video_analytics/:coursenum/')
  ),
  paginatePeople: makeUrlThunk(
    compile('/instructor/people.paginate_people/:coursenum/')
  ),
  getTableData: makeUrlThunk(
    compile('/instructor/people.get_people_json/:coursenum/')
  ),
  archiveSection: makeUrlThunk(
    compile('/instructor/people.archive_section/:coursenum/')
  ),
  bulkUpdateLmsIds: makeUrlThunk(
    compile('/instructor/people.bulk_fix_lms_ids/:coursenum/')
  ),
  bulkUploadGrades: makeUrlThunk(
    params => _bulkUploadGradesPath(params) + '?people=True'
  ),
}

export let payments = {
  createNewPayment: makeUrlThunk(
    compile('/account/admin_payments.create_new_payment/:coursenum')
  ),
  initiateTransfers: makeUrlThunk(
    compile('/account/admin_payments.initiate_transfer/:coursenum')
  ),
  createNewCommissions: makeUrlThunk(
    compile('/account/admin_payments.create_new_commissions/:coursenum')
  ),
  previewBatch: '/account/admin_payments.preview_batch_payments/',
  processBatch: '/account/admin_payments.process_batch_payments/',
}
export let activityInfoForm = {
  submit: makeUrlThunk(
    compile(
      '/instructor/new_activity_information.submit_endpoint/:coursenum/:guid/'
    )
  ),
}

export let connectTransfers = {
  getRecentTransfers: makeUrlThunk(
    compile('/account/royalty_reports.get_recent_transfers/:coursenum')
  ),
  getRecentTransfersStartingAfter: makeUrlThunk(
    compile(
      '/account/royalty_reports.get_recent_transfers_starting_after/:coursenum'
    )
  ),
  getRecentTransfersEndingBefore: makeUrlThunk(
    compile(
      '/account/royalty_reports.get_recent_transfers_ending_before/:coursenum'
    )
  ),
  exportRecentTransfers: makeUrlThunk(
    compile('/account/royalty_reports.export_recent_transfers/:coursenum')
  ),
}

export let symphonyServices = {
  updateAssessmentDeliveryService: makeUrlThunk(
    compile(
      '/instructor/admin_setup.update_symphony_assessment_delivery_service/:coursenum/'
    )
  ),
  pushCourseAssessmentsToSymphony: makeUrlThunk(
    compile(
      '/instructor/admin_setup.push_course_assessments_to_symphony/:coursenum/'
    )
  ),
  updateSymphonyData: makeUrlThunk(
    compile('/instructor/admin_setup.update_symphony_data/:coursenum/')
  ),
  pushCourseContentToSymphony: makeUrlThunk(
    compile(
      '/instructor/admin_setup.push_course_content_to_symphony/:coursenum/'
    )
  ),
}
export let canvasConnect = {
  getInstructorData(args) {
    let { contextId } = { ...defaults, ...args }
    return `/links/canvas_connect.get_instructor_data/-/${contextId}/`
  },
  createNewCourse: makeUrlThunk(
    compile('/links/canvas_connect.create_new_course/')
  ),
  updateCourseTitle: makeUrlThunk(
    compile('/links/canvas_connect.update_course_title/')
  ),
  postAssessments: makeUrlThunk(
    compile('/links/canvas_connect.post_assessments/')
  ),
  getAssessments(args) {
    let { coursenum, contextId } = { ...defaults, ...args }
    return `/links/canvas_connect.get_assessments/${coursenum}/${contextId}/`
  },
  getContextDetails(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.get_context_details/-/${contextId}/`
  },
  postDefaultLatePolicy() {
    return `/links/canvas_connect.post_default_late_policy/-/`
  },
  getDeepLinkInfo: makeUrlThunk(
    compile('/links/canvas_connect.get_deep_link_info/:coursenum/:contextId/')
  ),
  postDeepLinks: makeUrlThunk(compile('/links/canvas_connect.post_deep_link/')),
}

export let ltiOrgKeyManagement = {
  addNewOrg: makeUrlThunk(
    compile('/account/admin_lti_org_key_management.add_new_organization/')
  ),
  getOrgs: makeUrlThunk(
    compile('/account/admin_lti_org_key_management.get_all_organizations/')
  ),
  getOrgDetails(args) {
    let { orgId, pageNum } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.get_organization_details/-/${orgId}/?page=${pageNum}`
  },
  resetOrg() {
    return `/account/admin_lti_org_key_management.reset_organization_name_url/-/`
  },
  getContacts() {
    return `/account/admin_lti_org_key_management.get_sales_contacts/-/`
  },
  updateContact() {
    return `/account/admin_lti_org_key_management.update_org_contact/-/`
  },
  addDevKey(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.add_dev_key/-/${orgId}/`
  },
  addNewContext(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.add_new_context/-/${orgId}/`
  },
  getContextDetails(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.get_context_details/-/${contextId}/`
  },
  addNewCourse(args) {
    let { contextPk } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.add_new_course/-/${contextPk}/`
  },
  removeCourse(args) {
    let { contextCourseId } = { ...defaults, ...args }
    return `/account/admin_lti_org_key_management.remove_course/-/${contextCourseId}/`
  },
  // getContextActs(args) {
  //   let { coursenum, contextId } = { ...defaults, ...args }
  //   return `/account/admin_lti_org_key_management.get_context_acts/${coursenum}/${contextId}/`
  // },
  // updateActMapping(args) {
  //   let { coursenum, contextId } = { ...defaults, ...args }
  //   return `/account/admin_lti_org_key_management.update_act_mapping/${coursenum}/${contextId}/`
  // },
  // updateExternalUrl(args) {
  //   let { coursenum, contextId } = { ...defaults, ...args }
  //   return `/account/admin_lti_org_key_management.update_canvas_external_tool_url/${coursenum}/${contextId}/`
  // },
  // getContextStudents(args) {
  //   let { coursenum, contextId } = { ...defaults, ...args }
  //   return `/account/admin_lti_org_key_management.get_context_students/${coursenum}/${contextId}/`
  // },
  // updateStudentId(args) {
  //   let { coursenum, studentMyedId } = { ...defaults, ...args }
  //   return `/account/admin_lti_org_key_management.update_student_id/${coursenum}/${studentMyedId}/`
  // },
}

export let canvasOrgKeyManagement = {
  addNewOrg: makeUrlThunk(
    compile('/account/admin_org_key_management.add_new_organization/')
  ),
  getOrgs: makeUrlThunk(
    compile('/account/admin_org_key_management.get_all_organizations/')
  ),
  getOrgDetails(args) {
    let { orgId, pageNum } = { ...defaults, ...args }
    return `/account/admin_org_key_management.get_organization_details/-/${orgId}/?page=${pageNum}`
  },
  getContacts() {
    return `/account/admin_org_key_management.get_sales_contacts/-/`
  },
  updateContact() {
    return `/account/admin_org_key_management.update_org_contact/-/`
  },
  resetOrg(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.reset_organization_name_url/-/${orgId}`
  },
  addDevKey(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.add_dev_key/-/${orgId}/`
  },
  deleteDevKey(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.delete_dev_key/-/${orgId}/`
  },
  addOAuthKey(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.add_oauth_key/-/${orgId}/`
  },
  toggleOAuthKey(args) {
    let { orgId, keyId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.toggle_oauth_key/-/${orgId}/${keyId}/`
  },
  addNewContext(args) {
    let { orgId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.add_new_context/-/${orgId}/`
  },
  addNewCourse(args) {
    let { contextPk } = { ...defaults, ...args }
    return `/account/admin_org_key_management.add_new_course/-/${contextPk}/`
  },
  removeCourse(args) {
    let { contextCourseId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.remove_course/-/${contextCourseId}/`
  },
  getContextActs(args) {
    let { coursenum, contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.get_context_acts/${coursenum}/${contextId}/`
  },
  updateActMapping(args) {
    let { coursenum, contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.update_act_mapping/${coursenum}/${contextId}/`
  },
  updateExternalUrl(args) {
    let { coursenum, contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.update_canvas_external_tool_url/${coursenum}/${contextId}/`
  },
  getContextStudents(args) {
    let { coursenum, contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.get_context_students/${coursenum}/${contextId}/`
  },
  updateStudentId(args) {
    let { coursenum, studentMyedId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.update_student_id/${coursenum}/${studentMyedId}/`
  },
  getApiExplorerActions(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.get_api_explorer_actions/-/${contextId}/`
  },
  runApiExplorerAction(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.run_api_explorer_action/-/${contextId}/`
  },
  runApiExplorerRequest(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.run_api_explorer_request/-/${contextId}/`
  },
  getPostGradeOptions(args) {
    let { contextId, coursenum } = { ...defaults, ...args }
    const query = coursenum ? `?coursenum=${encodeURIComponent(coursenum)}` : ''
    return `/account/admin_org_key_management.get_post_grade_options/-/${contextId}/${query}`
  },
  saveActivityCanvasAssignmentId(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.save_activity_canvas_assignment_id/-/${contextId}/`
  },
  postGradeForUser(args) {
    let { contextId } = { ...defaults, ...args }
    return `/account/admin_org_key_management.post_grade_for_user/-/${contextId}/`
  },
}

export let simulationEndpoints = {
  loadProgress: makeUrlThunk(compile('/reader/simulations.load_progress/:coursenum/:activityPk/')),
  saveProgress: makeUrlThunk(compile('/reader/simulations.save_progress/:coursenum/:activityPk/')),
  submit: makeUrlThunk(compile('/reader/simulations.submit/:coursenum/:activityPk/')),
  newAttempt: makeUrlThunk(compile('/reader/simulations.new_attempt/:coursenum/:activityPk/')),
}

export let simulationEditorEndpoints = {
  generate: makeUrlThunk(compile('/instructor/simulations.generate/:coursenum/')),
}

export let adaExceptions = {
  createException: makeUrlThunk(
    compile('/instructor/ada_exceptions.create_exceptions/:coursenum/')
  ),
  deleteException: makeUrlThunk(
    compile('/instructor/ada_exceptions.delete_exception/:coursenum/')
  ),
}

export let LTIDashboard = {
  getConnection: makeUrlThunk(
    compile('/instructor/lti_dashboard.get_connection/')
  ),
  getResources: makeUrlThunk(
    compile('/instructor/lti_dashboard.get_resources/')
  ),
  getAssessments: makeUrlThunk(
    compile('/instructor/lti_dashboard.get_assessments/')
  ),
  getSubmissions: makeUrlThunk(
    compile('/instructor/lti_dashboard.get_submissions/')
  ),
}

export let LTICourseCreation = {
  addCourseObjects() {
    return `/links/lti.create_course_supporting_objects/`
  },
  checkStatus(args) {
    let { newCoursenum } = { ...args }
    return `/links/lti.get_course_setup_status/${newCoursenum}`
  },
}

export let codeOrderManagement = {
  generateReport() {
    return '/account/admin_coupons.download_order_report/'
  },
  pageOrders(args) {
    let { pageNum, filterString } = { ...args }
    return `/account/admin_coupons.page_orders/-/?page_num=${pageNum}&${filterString}`
  },
  fetchCodes(args) {
    let { orderId } = { ...args }
    return `/account/admin_coupons.fetch_codes/-/?order_id=${orderId}`
  },
  updateOrder() {
    return '/account/admin_coupons.update_order/-/'
  },
  updateCoupon() {
    return '/account/admin_coupons.update_coupon/-/'
  },
  calculate_projections() {
    return '/account/admin_coupons.calculate_projections/-/'
  },
  expireCode() {
    return '/account/admin_coupons.expire_code/-/'
  },
  checkCourseChange(args) {
    let { orderId, coursenum } = { ...args }
    return `/account/admin_coupons.check_course_change/-/?order_id=${encodeURIComponent(
      orderId
    )}&coursenum=${encodeURIComponent(coursenum)}`
  },
  changeCourse() {
    return '/account/admin_coupons.change_course/-/'
  },
}

export let billingQueue = {
  getQueue: (params = {}) => {
    const qs = new URLSearchParams()
    if (params.filter) qs.set('filter', params.filter)
    if (params.search) qs.set('search', params.search)
    const q = qs.toString()
    return `/account/billing_queue.get_queue/${q ? '?' + q : ''}`
  },
  getAssigned: (params = {}) => {
    const qs = new URLSearchParams()
    if (params.search) qs.set('search', params.search)
    const q = qs.toString()
    return `/account/billing_queue.get_assigned/${q ? '?' + q : ''}`
  },
  getSchools: () => '/account/billing_queue.get_schools/',
  getSchoolDetail: schoolId =>
    `/account/billing_queue.get_school_detail/?school_id=${schoolId}`,
  getInvoiceAccountDetail: accountId =>
    `/account/billing_queue.get_invoice_account_detail/?account_id=${accountId}`,
  assignCourse: () => '/account/billing_queue.assign_course/',
  updateBilling: () => '/account/billing_queue.update_billing/',
  deferCourse: () => '/account/billing_queue.defer_course/',
  undeferCourse: () => '/account/billing_queue.undefer_course/',
  markNotBillable: () => '/account/billing_queue.mark_not_billable/',
  setInvoiceNumber: () => '/account/billing_queue.set_invoice_number/',
  setPaymentStatus: () => '/account/billing_queue.set_payment_status/',
  createInvoiceAccount: () => '/account/billing_queue.create_invoice_account/',
  updateInvoiceAccount: () => '/account/billing_queue.update_invoice_account/',
  getCourseBilling: coursenum =>
    `/account/billing_queue.get_course_billing/?coursenum=${encodeURIComponent(coursenum)}`,
  adminAssignCourse: () => '/account/billing_queue.admin_assign_course/',
}

export const reportsEndpoints = {
  listScripts: () => '/account/admin_reporting.list_scripts/',
  recentTasks: () => '/account/admin_reporting.recent_tasks/',
  activities: coursenum => `/account/admin_reporting.activities/${coursenum}/`,
  runScript: () => '/account/admin_reporting.run_script/',
  taskStatus: taskId => `/account/admin_reporting.task_status/${taskId}/`,
  taskResult: taskId => `/account/admin_reporting.task_result/${taskId}/`,
}

export const tocAuthorEndpoints = {
  addItem: makeUrlThunk(compile('/author/tools.add_toc_item/:coursenum/')),
  renameItem: makeUrlThunk(compile('/author/tools.rename_toc_item/:coursenum/')),
  deleteItem: makeUrlThunk(compile('/author/tools.delete_toc_item/:coursenum/')),
  moveItem: makeUrlThunk(compile('/author/tools.move_toc_item/:coursenum/')),
}

// title page metadata editing (author tools on the reader's cover page)
export const bookInfoEndpoints = {
  updateBookInfo: makeUrlThunk(
    compile('/author/tools.update_book_info/:coursenum/')
  ),
  getContributors: makeUrlThunk(
    compile('/author/tools.get_book_contributors/:coursenum/')
  ),
  saveContributors: makeUrlThunk(
    compile('/author/tools.save_book_contributors/:coursenum/')
  ),
  quillOverviewPage: makeUrlThunk(
    compile('/author/tools.quill_book_overview/:coursenum/')
  ),
}
